import {VectorLayerDMTR} from "../../menu/view-menu/layer";
import {Projection} from "ol/proj";
import {FeatureType} from "../EnumTypes";
import GeoJSON from "ol/format/GeoJSON";
import {GritInputCondition, GritModel, GritOption, GritTableField} from "./grit.model";
import {GritFeature} from "./grit.feature";
import {GritCondition, GritConditionType} from "./grit.filter";
import {GritProjectService} from "../../services/grit/grit.project.service";
import {InterferenceTestDeviceModel} from "./grit.device-model";
import {GritDynamicTableType, InterferenceTestModelStates} from "./grit.enums";
import {NOTAM} from "./grit.notam";
import {GritCoordinates, GritCoordinateType} from "./grit.coordinates";

export class InterferenceTestModel implements GritModel<InterferenceTestModel> {
  id: number = 0;

  groupVisibility: string = "";
  groupAccess: string = "";
  groupOriginator: string = "";

  name: string = "";
  state: string = "";

  type: string = "";
  probability: number = 100;
  purpose: string = "";
  organization: string = "";

  dateStart: number;
  dateEnd: number;

  interruptionEmail: string = "";

  deviceMaskRadius: number = 0;

  testArea: any;
  firs: string[];

  calculationId: number;

  //Approval
  areaType: string;
  stakeholders: string[] = [];
  notamAccept: NOTAM;

  //Execution
  executed: boolean = false;
  metSpecifications: boolean = false;
  realDateStart: number = 0;
  realDateEnd: number = 0;
  disturbances: string;
  remarks: string;
  notamCancel: NOTAM;

  //frontend variables
  deviceMaskRadiusOriginal: number = 0;

  public static localDateTimeToISOString(date: Date) {
    return date.getFullYear() + '-' +
      String(date.getMonth() + 1).padStart(2, '0') + '-' +
      String(date.getDate()).padStart(2, '0') + 'T' +
      String(date.getHours()).padStart(2, '0') + ':' +
      String(date.getMinutes()).padStart(2, '0');
  }

  getState() {
    return InterferenceTestModel.fieldsValues.state.find(o => o.value === this.state)?.display ?? "";
  }

  get dateStartISO() {
    return this.dateStart == 0 ? '' : InterferenceTestModel.localDateTimeToISOString(new Date(this.dateStart)).slice(0, 16)
  }

  set dateStartISO(date: string) {
    let dateWithoutTimezone = null;
    if (date) {
      dateWithoutTimezone = date.replace(/([+-]\d{2}:\d{2})$/, '');
    }

    // Now parse the date as local time (since the timezone offset is removed)
    this.dateStart = new Date(dateWithoutTimezone).getTime();
  }

  get dateEndISO() {
    return this.dateEnd == 0 ? '' : InterferenceTestModel.localDateTimeToISOString(new Date(this.dateEnd)).slice(0, 16)
  }

  set dateEndISO(date: string) {
    let dateWithoutTimezone = null;
    if (date) {
      dateWithoutTimezone = date.replace(/([+-]\d{2}:\d{2})$/, '');
    }

    // Now parse the date as local time (since the timezone offset is removed)
    this.dateEnd = new Date(dateWithoutTimezone).getTime();
  }

  get realDateStartISO() {
    return this.realDateStart == 0 ? '' : InterferenceTestModel.localDateTimeToISOString(new Date(this.realDateStart)).slice(0, 16)
  }

  set realDateStartISO(date: string) {
    let dateWithoutTimezone = null;
    if (date) {
      dateWithoutTimezone = date.replace(/([+-]\d{2}:\d{2})$/, '');
    }

    // Now parse the date as local time (since the timezone offset is removed)
    this.realDateStart = new Date(dateWithoutTimezone).getTime();
  }

  get realDateEndISO() {
    return this.realDateEnd == 0 ? '' : InterferenceTestModel.localDateTimeToISOString(new Date(this.realDateEnd)).slice(0, 16)
  }

  set realDateEndISO(date: string) {
    let dateWithoutTimezone = null;
    if (date) {
      dateWithoutTimezone = date.replace(/([+-]\d{2}:\d{2})$/, '');
    }

    // Now parse the date as local time (since the timezone offset is removed)
    this.realDateEnd = new Date(dateWithoutTimezone).getTime();
  }

  public toDTO() {
    return {
      id: this.id,

      groupVisibility: this.groupVisibility,
      groupAccess: this.groupAccess,
      groupOriginator: this.groupOriginator,

      name: this.name,
      state: this.state,

      type: this.type,
      probability: this.probability,
      purpose: this.purpose,
      organization: this.organization,

      dateStart: this.dateStartISO,
      dateEnd: this.dateEndISO,

      interruptionEmail: this.interruptionEmail,

      deviceMaskRadius: this.deviceMaskRadius,

      testArea: this.testArea,
      firs: JSON.stringify(this.firs),

      calculationId: this.calculationId == 0 ? null : this.calculationId,

      //Approval
      areaType: this.areaType,
      stakeholders: JSON.stringify(this.stakeholders),

      //Execution
      executed: this.executed,
      metSpecifications: this.metSpecifications,
      realDateStart: this.realDateStartISO ? this.realDateStartISO : this.realDateStart,
      realDateEnd: this.realDateEndISO ? this.realDateEndISO : this.realDateEnd,
      disturbances: this.disturbances,
      remarks: this.remarks,
    }}

  public fromDTO(deviceDTO: any) {
    this.id = deviceDTO.id;

    this.groupVisibility = deviceDTO.groupVisibility;
    this.groupAccess = deviceDTO.groupAccess;
    this.groupOriginator = deviceDTO.groupOriginator;

    this.name = deviceDTO.name;
    this.state = deviceDTO.state;

    this.type = deviceDTO.type;
    this.probability = deviceDTO.probability;
    this.purpose = deviceDTO.purpose;
    this.organization = deviceDTO.organization;

    this.dateStartISO = deviceDTO.dateStart;
    this.dateEndISO = deviceDTO.dateEnd;

    this.interruptionEmail = deviceDTO.interruptionEmail;

    this.deviceMaskRadius = deviceDTO.deviceMaskRadius ?? 0;

    this.deviceMaskRadiusOriginal = deviceDTO.deviceMaskRadius ?? 0;

    this.testArea = deviceDTO.testArea;
    this.firs = JSON.parse(deviceDTO.firs ?? "[]");

    this.calculationId = deviceDTO.calculationId ?? undefined;

    //Approval
    this.areaType = deviceDTO.areaType ?? "normal";
    this.stakeholders = JSON.parse(deviceDTO.stakeholders) ?? [];

    let notamA = JSON.parse(deviceDTO.notamAccept)
    this.notamAccept = Object.assign(new NOTAM(),notamA );
    this.notamAccept.itemQ.center.latitude = new GritCoordinates(GritCoordinateType.Latitude, notamA?.itemQ.center.latitude._value);
    this.notamAccept.itemQ.center.longitude = new GritCoordinates(GritCoordinateType.Longitude, notamA?.itemQ.center.longitude._value);

    //Execution
    this.executed = deviceDTO.executed;
    this.metSpecifications = deviceDTO.metSpecifications;
    this.realDateStartISO = deviceDTO.realDateStart ?? deviceDTO.dateStart;
    this.realDateEndISO = deviceDTO.realDateEnd ?? deviceDTO.dateEnd;
    this.disturbances = deviceDTO.disturbances;
    this.remarks = deviceDTO.remarks;

    let notamC = JSON.parse(deviceDTO.notamCancel)
    this.notamCancel = Object.assign(new NOTAM(),notamC);
    this.notamCancel.itemQ.center.latitude = new GritCoordinates(GritCoordinateType.Latitude, notamC?.itemQ.center.latitude._value);
    this.notamCancel.itemQ.center.longitude = new GritCoordinates(GritCoordinateType.Longitude, notamC?.itemQ.center.longitude._value);

    return this;
  }

  public createFeature(layer: VectorLayerDMTR, projection: Projection): GritFeature<InterferenceTestModel> {
    return new GritFeature(this.id, {
      featureType: FeatureType.InterferenceTest,
      layer: layer,
      geometry: new GeoJSON().readGeometry(this.testArea, {
        dataProjection: "CRS:84",
        featureProjection: projection.getCode(),
      }),
      entity: this
    });
  }

  static fieldsValues: Record<string, GritOption<string>[]> = {
    state: [
      {value: InterferenceTestModelStates.Draft, display: "Draft"},
      {value: InterferenceTestModelStates.Approval, display: "Approval"},
      {value: InterferenceTestModelStates.Approved, display: "Approved"},
      {value: InterferenceTestModelStates.Archived_Refused, display: "Refused"},
      {value: InterferenceTestModelStates.Archived_Cancelled, display: "Cancelled"},
      {value: InterferenceTestModelStates.Archived_Complete, display: "Complete"},
    ],
    purpose: [
      {value: "practice", display: "Practice"},
      {value: "trial", display: "Trial"},
    ],
    organization: [
      {value: "civil", display: "Civil"},
      {value: "military", display: "Military"},
      {value: "law_enforcement", display: "Law Enforcement"},
    ],
    probability: [
      {value: "0", display: "0%"},
      {value: "25", display: "25%"},
      {value: "50", display: "50%"},
      {value: "75", display: "75%"},
      {value: "100", display: "100%"},
    ],
  }

  static configuration: Record<string, GritOption<any>[]> = {
    table: [
      {
        display: "Name",
        value: {
          type: GritDynamicTableType.BASIC,
          fieldValue : function (entity: InterferenceTestModel) {
            return entity.name;
          }
        }
      },
      {
        display: "State",
        value: {
          type: GritDynamicTableType.BASIC,
          fieldValue : function (entity: InterferenceTestModel) {
            return InterferenceTestModel.fieldsValues.state.find(state => state.value == entity.state)?.display
          }
        },
      },
      {
        display: "Type",
        value: {
          type: GritDynamicTableType.BASIC,
          fieldValue : function (entity: InterferenceTestModel) {
            return entity.type
          }
        },
      },
      {
        display: "Probability",
        value: {
          type: GritDynamicTableType.EXTENDED,
          fieldValue : function (entity: InterferenceTestModel) {
            return InterferenceTestModel.fieldsValues.probability
              .find(state => state.value == String(entity.probability))?.display
          }
        },
      },
      {
        display: "Purpose",
        value: {
          type: GritDynamicTableType.EXTENDED,
          fieldValue : function (entity: InterferenceTestModel) {
            return InterferenceTestModel.fieldsValues.purpose
              .find(state => state.value == entity.purpose)?.display
          }
        },
      },
      {
        display: "Organization",
        value: {
          type: GritDynamicTableType.EXTENDED,
          fieldValue : function (entity: InterferenceTestModel) {
            return InterferenceTestModel.fieldsValues.organization
              .find(state => state.value == entity.organization)?.display
          }
        },
      },
      {
        display: "Start Date",
        value: {
          type: GritDynamicTableType.EXTENDED,
          fieldValue : function (entity: InterferenceTestModel) {
            return GritProjectService.globalInformation.format.format(new Date(entity.dateStart))
          }
        }
      },
      {
        display: "End Date",
        value: {
          type: GritDynamicTableType.EXTENDED,
          fieldValue : function (entity: InterferenceTestModel) {
            return GritProjectService.globalInformation.format.format(new Date(entity.dateEnd))
          }
        }
      },
      {
        display: "FIRS",
        value: {
          type: GritDynamicTableType.COMPLETE,
          fieldValue : function (entity: InterferenceTestModel) {
            return entity.firs?.map(fir => GritProjectService.globalInformation.firs
                .find(o => o.value == fir)?.display).toString()
          }
        }
      },
    ] as GritOption<GritTableField<InterferenceTestModel>>[],

    filters: [
      {
        display: "Dates",
        value: {
          inputs: [
            {
              field: "Start Date",
              type: "datetime-local",
              value: () => "",
            },
            {
              field: "End Date",
              type: "datetime-local",
              value: () => "",
            },
          ],
          condition: function (values: string[]): GritCondition[] {
            let start = new Date(values[0]).getTime();
            let end = new Date(values[1]).getTime();

            let condition1 = new GritCondition("dateStart", GritConditionType.LowerThanEqual, end);
            let condition2 = new GritCondition("dateEnd", GritConditionType.GreaterThanEqual, start);
            return [condition1, condition2]
          },

          toString(conditions : GritCondition[]) {
            let start = new Date(conditions[0].value);
            let end = new Date(conditions[1].value);
            let a = GritProjectService.globalInformation.format
            return `${a.format(start)} < now < ${a.format(end)}`
          }
        }
      },
      {
        display: "Purpose",
        value: {
          inputs: [
            {
              field: "Value",
              type: "dropdown",
              value: () => InterferenceTestModel.fieldsValues["purpose"] ?? [],
            },
          ],
          condition: function (values: string[]): GritCondition[] {
            let condition = new GritCondition("purpose", GritConditionType.Equal, values[0]);
            return [condition]
          },
          toString(conditions: GritCondition[]): string {
            return InterferenceTestModel.fieldsValues.purpose.find(option => option.value == conditions[0].value).display ?? "";
          }
        }
      },
      {
        display: "Organization",
        value: {
          inputs: [
            {
              field: "Value",
              type: "dropdown",
              value: () => InterferenceTestModel.fieldsValues["organization"] ?? [],
            },
          ],
          condition: function (values: string[]): GritCondition[] {
            let condition = new GritCondition("organization", GritConditionType.Equal, values[0]);
            return [condition]
          },
          toString(conditions: GritCondition[]): string {
            return InterferenceTestModel.fieldsValues.organization.find(option => option.value == conditions[0].value).display ?? "";
          }
        }
      },
      {
        display: "FIR",
        value: {
          inputs: [
            {
              field: "Value",
              type: "dropdown",
              value: () => GritProjectService.globalInformation.firs ?? [],
            },
          ],
          condition: function (values: string[]): GritCondition[] {
            let condition = new GritCondition("firs", GritConditionType.Includes_Equal, values[0]);
            return [condition]
          },
          toString(conditions: GritCondition[]): string {
            return GritProjectService.globalInformation.firs.find(option => option.value == conditions[0].value).display ?? "";
          }
        }
      },
      {
        display: "Country",
        value: {
          inputs: [
            {
              field: "Value",
              type: "dropdown",
              value: () => GritProjectService.globalInformation.countries ?? [],
            },
          ],
          condition: function (values: string[]): GritCondition[] {
            let condition = new GritCondition("firs", GritConditionType.Includes_StartsWith, values[0]);
            return [condition]
          },
          toString(conditions: GritCondition[]): string {
            return GritProjectService.globalInformation.countries.find(option => option.value == conditions[0].value).display ?? "";
          }
        }
      },
    ] as GritOption<GritInputCondition>[],

    search: [
      {
        display: "All",
        value: {
          inputs: [],
          condition: function (values: string[]): GritCondition[] {
            let condition = new GritCondition("state", GritConditionType.StartsWith, "");
            return [condition]
          }
        }
      },
      {
        display: "Start Date",
        value: {
          inputs: [
            {
              field: "Greater Than",
              type: "datetime-local",
              value: () =>  "",
            },
          ],
          condition: function (values: string[]): GritCondition[] {
            let date = new Date(values[0]).getTime();
            let condition = new GritCondition("dateStart", GritConditionType.GreaterThanEqual, date);
            return [condition]
          }
        }
      },
      {
        display: "End Date",
        value: {
          inputs: [
            {
              field: "Lower Than",
              type: "datetime-local",
              value: () => "",
            },
          ],
          condition: function (values: string[]): GritCondition[] {
            let date = new Date(values[0]).getTime();
            let condition = new GritCondition("dateEnd", GritConditionType.LowerThanEqual, date);
            return [condition]
          }
        }
      },
      {
        display: "Organization",
        value: {
          inputs: [
            {
              field: "Value",
              type: "dropdown",
              value: () => InterferenceTestModel.fieldsValues["organization"] ?? [],
            },
          ],
          condition: function (values: string[]): GritCondition[] {
            let condition = new GritCondition("organization", GritConditionType.Equal, values[0]);
            return [condition]
          },
          toString(conditions: GritCondition[]): string {
            return InterferenceTestModel.fieldsValues.organization.find(option => option.value == conditions[0].value).display ?? "";
          }
        }
      },
      {
        display: "Purpose",
        value: {
          inputs: [
            {
              field: "Value",
              type: "dropdown",
              value: () => InterferenceTestModel.fieldsValues["purpose"] ?? [],
            },
          ],
          condition: function (values: string[]): GritCondition[] {
            let condition = new GritCondition("purpose", GritConditionType.Equal, values[0]);
            return [condition]
          },
          toString(conditions: GritCondition[]): string {
            return InterferenceTestModel.fieldsValues.purpose.find(option => option.value == conditions[0].value).display ?? "";
          }
        }
      },
      {
        display: "FIR",
        value: {
          inputs: [
            {
              field: "Value",
              type: "dropdown",
              value: () => GritProjectService.globalInformation.firs ?? [],
            },
          ],
          condition: function (values: string[]): GritCondition[] {
            let condition = new GritCondition("firs", GritConditionType.Includes_Equal, values[0]);
            return [condition]
          },
          toString(conditions: GritCondition[]): string {
            return GritProjectService.globalInformation.firs.find(option => option.value == conditions[0].value).display ?? "";
          }
        }
      },
      {
        display: "Country",
        value: {
          inputs: [
            {
              field: "Value",
              type: "dropdown",
              value: () => GritProjectService.globalInformation.countries ?? [],
            },
          ],
          condition: function (values: string[]): GritCondition[] {
            let condition = new GritCondition("firs", GritConditionType.Includes_StartsWith, values[0]);
            return [condition]
          },
          toString(conditions: GritCondition[]): string {
            return GritProjectService.globalInformation.countries.find(option => option.value == conditions[0].value).display ?? "";
          }
        }
      },
    ] as GritOption<GritInputCondition>[]
  }
}

export class InterferenceTestEntities {
  devices : InterferenceTestDeviceModel[] = [];

  fromDTO(interferenceTestEntities: any){
    (interferenceTestEntities.devices as []).forEach(device =>
      this.devices.push(new InterferenceTestDeviceModel().fromDTO(device)));

    return this;
  }
}
